import { Auth0Provider, useAuth0 } from "@auth0/auth0-react";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

type LoginMode = "login" | "signup" | "google" | "apple";

export type CustomerUser = {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  picture: string;
  givenName: string;
  familyName: string;
};

type CustomerAuthValue = {
  enabled: boolean;
  loading: boolean;
  authenticated: boolean;
  error: string;
  user: CustomerUser | null;
  login: (mode?: LoginMode) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string>;
};

type AuthConfig = {
  enabled: boolean;
  domain?: string;
  clientId?: string;
  audience?: string;
};

async function unavailable(): Promise<never> {
  throw new Error("Customer accounts are not configured yet.");
}

const disabledAuth: CustomerAuthValue = {
  enabled: false,
  loading: false,
  authenticated: false,
  error: "",
  user: null,
  login: unavailable,
  logout: async () => undefined,
  getAccessToken: unavailable,
};

const CustomerAuthContext = createContext<CustomerAuthValue>(disabledAuth);

function AuthBridge({ audience, children }: { audience: string; children: ReactNode }) {
  const auth = useAuth0();
  useEffect(() => {
    if (!auth.error || window.location.pathname.startsWith("/account")) return;
    const callbackError = new URLSearchParams(window.location.search).has("error");
    if (!callbackError) return;
    window.history.replaceState({}, document.title, "/account");
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [auth.error]);
  const login = useCallback(async (mode: LoginMode = "login") => {
    const authorizationParams: { screen_hint?: string; connection?: string } = {};
    if (mode === "signup") authorizationParams.screen_hint = "signup";
    if (mode === "google") authorizationParams.connection = "google-oauth2";
    if (mode === "apple") authorizationParams.connection = "apple";
    await auth.loginWithRedirect({ appState: { returnTo: "/account" }, authorizationParams });
  }, [auth.loginWithRedirect]);
  const logout = useCallback(async () => {
    await auth.logout({ logoutParams: { returnTo: `${window.location.origin}/` } });
  }, [auth.logout]);
  const getAccessToken = useCallback(() => auth.getAccessTokenSilently({
    authorizationParams: { audience },
  }), [audience, auth.getAccessTokenSilently]);
  const user = useMemo<CustomerUser | null>(() => auth.user ? {
    id: auth.user.sub || "",
    name: auth.user.name || auth.user.nickname || "ROAM customer",
    email: auth.user.email || "",
    emailVerified: auth.user.email_verified === true,
    picture: auth.user.picture || "",
    givenName: typeof auth.user.given_name === "string" ? auth.user.given_name : "",
    familyName: typeof auth.user.family_name === "string" ? auth.user.family_name : "",
  } : null, [auth.user]);
  const value = useMemo<CustomerAuthValue>(() => ({
    enabled: true,
    loading: auth.isLoading,
    authenticated: auth.isAuthenticated,
    error: auth.error?.message || "",
    user,
    login,
    logout,
    getAccessToken,
  }), [auth.error, auth.isAuthenticated, auth.isLoading, getAccessToken, login, logout, user]);
  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
}

export function AuthRoot({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  useEffect(() => {
    let active = true;
    fetch("/api/auth/config")
      .then(async (response) => response.ok ? response.json() as Promise<AuthConfig> : { enabled: false })
      .then((value) => { if (active) setConfig(value); })
      .catch(() => { if (active) setConfig({ enabled: false }); });
    return () => { active = false; };
  }, []);
  if (!config) return <div className="auth-bootstrap"><span>ROAM</span><i /></div>;
  if (!config.enabled || !config.domain || !config.clientId || !config.audience) {
    return <CustomerAuthContext.Provider value={disabledAuth}>{children}</CustomerAuthContext.Provider>;
  }
  return <Auth0Provider
    domain={config.domain}
    clientId={config.clientId}
    authorizationParams={{
      redirect_uri: `${window.location.origin}/`,
      audience: config.audience,
      scope: "openid profile email",
    }}
    cacheLocation="memory"
    onRedirectCallback={(state) => {
      const returnTo = typeof state?.returnTo === "string"
        && state.returnTo.startsWith("/")
        && !state.returnTo.startsWith("//")
        ? state.returnTo
        : "/account";
      window.history.replaceState({}, document.title, returnTo);
      window.dispatchEvent(new PopStateEvent("popstate"));
    }}
  >
    <AuthBridge audience={config.audience}>{children}</AuthBridge>
  </Auth0Provider>;
}

export function useCustomerAuth() {
  return useContext(CustomerAuthContext);
}
